<section id="pricing" class="py-24 bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 transition-colors duration-300">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Error Message -->
    @if (errorMessage()) {
      <div class="mb-8 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-center">
        <p class="text-sm text-red-600 dark:text-red-400">{{ errorMessage() }}</p>
      </div>
    }

    <div class="text-center max-w-3xl mx-auto mb-16 observe-viewport effect-fade-up">
      <h2 class="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white mb-4">
        Strategic Pricing for Strategic Advisors
      </h2>
      <p class="text-lg text-slate-500 dark:text-slate-400">
        Wherever you're going, there's a plan to get you there.
      </p>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
      @for (plan of plans; track plan; let i = $index) {
        <div
           [class]="plan.popular 
             ? 'rounded-2xl border border-indigo-200 dark:border-indigo-600 bg-slate-50 dark:bg-slate-900 p-8 shadow-md relative observe-viewport effect-fade-up delay-200 flex flex-col h-full' 
             : 'rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 hover:shadow-lg transition-all observe-viewport effect-fade-up flex flex-col h-full'"
          [class.delay-100]="i === 0"
          [class.delay-300]="i === 2">
          <!-- Popular Badge -->
          @if (plan.popular) {
            <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
              {{ plan.comingSoon ? 'Coming Soon' : 'Most Popular' }}
            </div>
          }
          <h3 class="text-xl font-semibold text-slate-900 dark:text-white mb-2">{{ plan.name }}</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">{{ plan.description }}</p>
          <div class="mb-6">
            <span class="text-4xl font-bold text-slate-900 dark:text-white">{{ plan.price }}</span>
            @if (plan.period) {
              <span class="text-slate-500 dark:text-slate-400">{{ plan.period }}</span>
            }
            @if (plan.badge) {
              <div class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 inline-block px-2 py-1 rounded mt-2">
                {{ plan.badge }}
              </div>
            }
          </div>
          <ul class="space-y-4 mb-4 flex-1">
            @for (feature of plan.features; track feature; let isFirst = $first) {
              <li class="flex gap-3 text-sm text-slate-600 dark:text-slate-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" class="text-indigo-600 dark:text-indigo-400 shrink-0">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span [class.font-semibold]="isFirst && (plan.name === 'Professional' || plan.name === 'Enterprise')"
                  [class.text-slate-900]="isFirst && (plan.name === 'Professional' || plan.name === 'Enterprise')"
                  [class.dark:text-white]="isFirst && (plan.name === 'Professional' || plan.name === 'Enterprise')">
                  {{ feature }}
                </span>
              </li>
            }
          </ul>
          @if (plan.note) {
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-4 italic">{{ plan.note }}</p>
          }
          <button (click)="handlePlanAction(plan)"
            [disabled]="plan.comingSoon || isLoading()"
                [class]="plan.comingSoon || isLoading()
                  ? 'w-full py-3 rounded-lg bg-slate-300 dark:bg-slate-700 font-medium text-slate-500 dark:text-slate-400 cursor-not-allowed'
                  : plan.popular 
                    ? 'w-full py-3 rounded-lg bg-indigo-600 font-medium text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 dark:shadow-indigo-500/30 transition-all' 
                    : 'w-full py-3 rounded-lg border border-slate-200 dark:border-slate-700 font-medium text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors'">
            @if (isLoading() && plan.name === 'Standard') {
              <span class="inline-flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            } @else {
              {{ plan.cta }}
            }
          </button>
        </div>
      }
    </div>
    <div class="text-center mt-12 observe-viewport effect-fade-up">
      <a href="/documents/pricing" target="_blank" rel="noopener noreferrer"
        class="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 dark:shadow-indigo-500/20 hover:from-indigo-700 hover:to-indigo-800 hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-300">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
        </svg>
        View Platform Pricing
      </a>
    </div>
  </div>
</section>

